/**
 * The one payload builder for `POST /workspaces/publish-local`.
 *
 * ## Why this module exists
 *
 * Two call sites send this endpoint a workspace, and until 27.08.2026 each had
 * its own hand-copied copy of the payload shape:
 *
 *  - `services/localWorkspaceStore.js` → `buildPublishLocalPayload`, for
 *    publishing or forking a *local* workspace into the signed-in account;
 *  - `components/ImportNetworkModal.js` → `buildImportPayload`, for importing a
 *    package downloaded from the public API Network.
 *
 * The twelve-field request map was byte-identical in both. Two builders for one
 * endpoint is how one of them gets a fix and the other does not — which is
 * exactly what happened on the server side of this same feature (see the note
 * in `PublicApis.sanitize_request/1`: `auth` was scrubbed while `headers`,
 * `params`, `cookies` and `body` went out verbatim). They are merged here so a
 * future change to the wire shape lands once.
 *
 * ## Credentials travel here, deliberately
 *
 * Everything the endpoint receives is a **same-account transfer**: it creates a
 * workspace owned by the caller, in the caller's own account, visible to nobody
 * else. `auth`, `headers`, `params`, `cookies`, `body` and `settings` are passed
 * through verbatim **on purpose** — this is cloud sync with a different entry
 * point, and a copy of your own workspace with its credentials stripped is a
 * broken copy.
 *
 * Do **not** wire `utils/shareRedaction.js` into this module. That redactor
 * belongs at the *share* boundary, where a workspace crosses to somebody else.
 * Settled 26.08.2026 in the share-in-chat task's plan — *"`prepareRequestBody-
 * ForApi` untouched, so cloud sync keeps working credentials — correct call,
 * redaction stays at the share boundary"* — and re-confirmed by the architect's
 * open-findings review on 27.08.2026, which found the "publish-local leaks
 * credentials" finding did not reproduce as framed.
 *
 * `src/tests/publishLocalPayload.test.js` holds a negative control that asserts
 * the credentials ARE still present. If that test ever goes red because someone
 * "fixed" a leak here, read this docblock before deleting the assertion.
 *
 * ## `visibility` is the whole threat model, so it is not a parameter
 *
 * A same-account transfer is safe *because the result is private*. `visibility`
 * was the single value standing between this payload and a completely different
 * threat model: the backend takes `attrs["visibility"]` verbatim
 * (`BackendPheonix.Workspaces.publish_local/2`), so a `'public'` here would make
 * the user's credential-bearing workspace publicly discoverable in one field.
 *
 * The old builder accepted `options.visibility` and honoured `'public'`. Nothing
 * ever passed it — every call site hard-coded `'private'` — so the branch was
 * dead code holding a credential hole open for whoever wired a UI to it next. It
 * is now a constant. Going public is a **separate, server-side, already-redacted
 * route**: `POST /workspaces/:id/publish-to-network` →
 * `PublicApis.import_package/1` → `PublicApis.Redactor`. If a "make this public"
 * feature is ever wanted, it goes through there, not through a flag here.
 *
 * `src/tests/publishLocalPayload.test.js` source-scans this file and both call
 * sites for a re-introduced `visibility: 'public'`.
 */

/** The client's canonical order for finding a collection's variables:
 *  `variable_scopes` first, then `settings`. Matches `utils/collectionTabState.js`,
 *  `hooks/useVariableHydration.js` and `utils/workspaceRealtime.js`. */
export const collectionVariablesFromScopes = (collection) =>
  collection?.variable_scopes?.collection_variables ??
  collection?.settings?.collection_variables ??
  [];

/** The API Network package shape puts them at the top level instead —
 *  `PublicApis.sanitize_collection/1` emits no `variable_scopes` at all. Kept as
 *  a separate resolver rather than folded into one chain: merging the orders
 *  would silently change which value wins when a collection carries both. */
export const collectionVariablesFromPackage = (collection) =>
  collection?.collection_variables ?? collection?.settings?.collection_variables ?? [];

const mapRequest = (r, rIdx) => ({
  name: r.name || `Request ${rIdx + 1}`,
  description: r.description ?? '',
  method: r.method || 'GET',
  url: r.url ?? '',
  // Credential carriers, verbatim and on purpose — see the moduledoc.
  headers: r.headers ?? [],
  params: r.params ?? [],
  cookies: r.cookies ?? [],
  body: r.body ?? '',
  body_type: r.body_type ?? r.bodyType ?? 'json',
  auth: r.auth ?? {},
  settings: r.settings ?? {},
  sort_order: typeof r.sort_order === 'number' ? r.sort_order : rIdx,
});

/**
 * Build the request body for `POST /workspaces/publish-local`.
 *
 * The caller resolves its own source shape into these arguments; everything
 * below this line — defaults, field names, ordering, and the private
 * `visibility` — is shared.
 *
 * The output is an explicit allowlist of keys, which is what keeps local-only
 * bookkeeping (`_deviceLocal`, `_localWorkspaceId`, `cloud_collection_id`,
 * `link_status`, `last_synced_at`, `local_uid`) off the wire even though the
 * collection objects handed in still carry it.
 *
 * @param {object}   args
 * @param {string}   args.name              already-resolved workspace name
 * @param {string}   args.description       already-resolved description
 * @param {object}   [args.variableScopes]  `{ globals, workspace_globals, environments }`
 * @param {Array}    [args.collections]     source collections, any extra keys ignored
 * @param {Function} [args.collectionVariablesOf] how to find a collection's variables
 */
export function buildWorkspacePublishPayload({
  name,
  description,
  variableScopes = {},
  collections,
  collectionVariablesOf = collectionVariablesFromScopes,
}) {
  return {
    name,
    description,
    // Not a parameter, and not a branch. See the moduledoc.
    visibility: 'private',
    variable_scopes: {
      globals: variableScopes.globals || [],
      workspace_globals: variableScopes.workspace_globals || [],
      environments: variableScopes.environments || [],
    },
    collections: (collections || []).map((c, cIdx) => ({
      name: c.name || `Collection ${cIdx + 1}`,
      description: c.description ?? '',
      sort_order: typeof c.sort_order === 'number' ? c.sort_order : cIdx,
      settings: c.settings ?? {},
      collection_variables: collectionVariablesOf(c),
      requests: (c.requests || []).map(mapRequest),
    })),
  };
}
